/**
 * Fastest and longest estimated-work totals across open choices.
 * Answered decisions keep only the selected branch. Parallel missions that
 * are not mutually exclusive are summed. Nested campaigns contribute their own range.
 */

import { DEFAULT_STATUSES } from '../../TaskNode/constants';
import { getEffectiveDurationMinutes } from '../../../../../utils/taskDuration';
import { buildChoiceSearch, missionTasksOf } from './choiceScenarios';
import { isTaskCompleteForProgress } from './taskCompletion';

function getTaskDisplayName(task) {
  const name = task?.name?.trim();
  return name || 'Untitled';
}

function buildBreakdownItem(task, pathPrefix, minutes, kind = 'mission', extra = {}) {
  const minMinutes = extra.minMinutes ?? minutes;
  const maxMinutes = extra.maxMinutes ?? minutes;
  return {
    id: kind === 'unloaded-nested' ? `${task.id}-unloaded` : task.id,
    name: getTaskDisplayName(task),
    path: pathPrefix,
    minutes,
    minMinutes,
    maxMinutes,
    kind,
    conditional: !!extra.conditional,
  };
}

function emptyWorkRange() {
  return { minMinutes: 0, maxMinutes: 0, missingEstCount: 0, incompleteCount: 0, items: [] };
}

function skippedPart() {
  return { min: 0, max: 0, missing: 0, incomplete: 0, items: [], skip: true };
}

function missingPart(task, pathPrefix, kind = 'mission') {
  return {
    min: 0,
    max: 0,
    missing: 1,
    incomplete: 1,
    items: [buildBreakdownItem(task, pathPrefix, null, kind)],
    skip: false,
  };
}

export function taskWorkPart(task, ctx) {
  if (task.nodeType === 'decision') return skippedPart();
  if (ctx.remainingOnly && isTaskCompleteForProgress(task, ctx.statuses)) return skippedPart();

  if (task.nestedSprintId) return nestedWorkPart(task, ctx);

  const minutes = getEffectiveDurationMinutes(task);
  if (minutes == null) return missingPart(task, ctx.pathPrefix);
  return {
    min: minutes,
    max: minutes,
    missing: 0,
    incomplete: 1,
    items: [buildBreakdownItem(task, ctx.pathPrefix, minutes)],
    skip: false,
  };
}

function nestedWorkPart(task, ctx) {
  if (ctx.seenSprintIds.has(task.nestedSprintId)) {
    return missingPart(task, ctx.pathPrefix, 'unloaded-nested');
  }

  const nestedSprint = ctx.nestedSprints.find((sprint) => sprint.id === task.nestedSprintId)
    || ctx.allSprints.find((sprint) => sprint.id === task.nestedSprintId)
    || null;

  if (!nestedSprint) {
    if (ctx.remainingOnly && isTaskCompleteForProgress(task, ctx.statuses)) return skippedPart();
    return missingPart(task, ctx.pathPrefix, 'unloaded-nested');
  }

  const nestedTasks = nestedSprint.tasks;
  if (nestedTasks && nestedTasks.length > 0) {
    const nextSeen = new Set(ctx.seenSprintIds);
    nextSeen.add(nestedSprint.id);
    const nestedStatuses = nestedSprint.statuses || DEFAULT_STATUSES;
    const grandchildSprints = ctx.allSprints.filter((sprint) => sprint.parentSprintId === nestedSprint.id);
    const nested = calculateWorkTimeRange(
      nestedTasks,
      nestedStatuses,
      grandchildSprints,
      ctx.allSprints,
      {
        remainingOnly: ctx.remainingOnly,
        pathPrefix: [...ctx.pathPrefix, getTaskDisplayName(task)],
        seenSprintIds: nextSeen,
      },
    );
    return {
      min: nested.minMinutes,
      max: nested.maxMinutes,
      missing: nested.missingEstCount,
      incomplete: nested.incompleteCount,
      items: nested.items,
      skip: false,
    };
  }

  if (!Array.isArray(nestedTasks) && (nestedSprint.progress || 0) < 100) {
    if (ctx.remainingOnly && isTaskCompleteForProgress(task, ctx.statuses)) return skippedPart();
    return missingPart(task, ctx.pathPrefix, 'unloaded-nested');
  }

  return { min: 0, max: 0, missing: 0, incomplete: 0, items: [], skip: false };
}

export function buildWorkParts(missionTasks, ctx) {
  const parts = new Map();
  for (const task of missionTasks) {
    parts.set(task.id, taskWorkPart(task, ctx));
  }
  return parts;
}

/**
 * @returns {{ minMinutes: number, maxMinutes: number, missingEstCount: number, incompleteCount: number, items: Array }}
 */
export function calculateWorkTimeRange(
  tasks,
  statuses = DEFAULT_STATUSES,
  nestedSprints = [],
  allSprints = [],
  {
    remainingOnly = false,
    pathPrefix = [],
    seenSprintIds = new Set(),
  } = {},
) {
  const missionTasks = missionTasksOf(tasks);
  if (missionTasks.length === 0) return emptyWorkRange();

  const partCtx = {
    statuses,
    nestedSprints,
    allSprints,
    remainingOnly,
    pathPrefix,
    seenSprintIds,
  };
  const parts = buildWorkParts(missionTasks, partCtx);
  const search = buildChoiceSearch(missionTasks);
  const assignments = search.listAssignments(parts);

  let minMinutes = Infinity;
  let maxMinutes = -Infinity;
  const onEvery = new Set(missionTasks.map((task) => task.id));
  const onSome = new Set();

  for (const assignment of assignments) {
    const excluded = search.exclude(assignment);
    let scenarioMin = 0;
    let scenarioMax = 0;
    for (const task of missionTasks) {
      if (excluded.has(task.id)) {
        onEvery.delete(task.id);
        continue;
      }
      onSome.add(task.id);
      const part = parts.get(task.id);
      if (!part || part.skip) continue;
      scenarioMin += part.min;
      scenarioMax += part.max;
    }
    if (scenarioMin < minMinutes) minMinutes = scenarioMin;
    if (scenarioMax > maxMinutes) maxMinutes = scenarioMax;
  }

  if (!Number.isFinite(minMinutes)) minMinutes = 0;
  if (!Number.isFinite(maxMinutes)) maxMinutes = 0;

  let missingEstCount = 0;
  let incompleteCount = 0;
  const items = [];
  for (const task of missionTasks) {
    if (!onSome.has(task.id)) continue;
    const part = parts.get(task.id);
    if (!part || part.skip) continue;
    missingEstCount += part.missing;
    incompleteCount += part.incomplete;
    const forceConditional = !onEvery.has(task.id);
    for (const item of part.items) {
      items.push(forceConditional ? { ...item, conditional: true } : item);
    }
  }

  return { minMinutes, maxMinutes, missingEstCount, incompleteCount, items };
}
